import * as df from 'date-fns';
import * as React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import BooksTextButton from '../../../../../molecules/merchant/resources/book/common/buttons/BooksTextButton';
import BooksTimeItem from '../../../../../molecules/merchant/resources/book/common/texts/BooksTimeItem';
import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import BooksSelectedMenu from '../../../../../molecules/merchant/resources/book/BooksSelectedMenu';
import InputSchoolCalendarDateTimePicker from '../../../../../molecules/merchant/resources/book/InputSchoolCalendarDateTimePicker';

import * as IBooks from '../../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../../interfaces/merchants/resources/books/inputs';
import * as Actions from '../../../../../redux/actions/merchants/resources/books';
import { DateTimePickerQuery } from '../../../../../redux/actions/merchants/resources/books/api';
import * as Inputs from '../../../../../redux/actions/merchants/resources/books/inputs';
import * as B from '../../../../../redux/reducers/merchants/resources/books';
import * as I from '../../../../../redux/reducers/merchants/resources/books/inputs';

import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import { RSButton } from 'src/reservation-page/RSButton';

interface ElementProps {
  resourcePublicId: string;
  settings: IBooks.SettingBookingDateTimePicker;
  postable: boolean;
  backToModifyPage: () => void;
  onSubmit: () => void;
  onRestart: () => void;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    clearDateTimePicker: () => void;
    setDateTimePicker: (q: DateTimePickerQuery) => Promise<void>;
    handleSubmit: (
      reservationId: number,
      schoolTimeSlots: IInputs.SchoolTimeSlot[],
      merchantPublicId: string,
    ) => Promise<void>;
    handleClearTargets: (course: boolean) => void;
  };
}

type Props = ElementProps & StateProps & DispatchProps & WrappedComponentProps;

interface State {
  baseDate: Date;
  selecting: boolean;
  selectedSchoolTimeSlots?: IBooks.SchoolTimeSlot[];
}

class InputSchoolCalendar extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      settings: { selectedTimeSlot, selectedLessonId },
      inputs: { lesson, schoolTimeSlots },
    } = this.props;
    const selectedSchoolTimeSlots = schoolTimeSlots
      ? schoolTimeSlots
      : selectedTimeSlot && lesson!.id === selectedLessonId
        ? [selectedTimeSlot]
        : undefined;
    this.state = {
      baseDate: new Date(),
      selecting: false,
      selectedSchoolTimeSlots,
    };
    this.handleChangeTarget = this.handleChangeTarget.bind(this);
    this.handleStartSelectingDate = this.handleStartSelectingDate.bind(this);

    this.handleChangeBaseDate = this.handleChangeBaseDate.bind(this);
    this.handleChangeTimeSlots = this.handleChangeTimeSlots.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public async componentDidMount() {
    scrollToTop();
    const { selectedSchoolTimeSlots } = this.state;
    this.props.actions.clearDateTimePicker();
    const today = selectedSchoolTimeSlots
      ? df.parse(selectedSchoolTimeSlots[0].date)
      : new Date();
    this.handleChangeBaseDate(today);
  }

  private handleChangeBaseDate(baseDate: Date) {
    const { actions, inputs } = this.props;
    const { merchantId, lesson } = inputs;

    if (!lesson || !merchantId) {
      return;
    }

    this.setState({ ...this.state, baseDate });

    actions.setDateTimePicker({
      merchantId,
      resourceId: this.props.resourcePublicId,
      lesson,
      start: df.startOfMonth(baseDate),
      end: df.endOfMonth(baseDate),
    });
  }

  private handleStartSelectingDate() {
    this.setState({
      ...this.state,
      selecting: true,
    });
  }

  private handleChangeTarget(lesson: boolean, _option: boolean) {
    this.props.actions.handleClearTargets(lesson);
    this.props.backToModifyPage();
  }

  private handleChangeTimeSlots(timeSlots: IBooks.SchoolTimeSlot[]) {
    const timeSlot = timeSlots[0];
    this.setState({
      ...this.state,
      selecting: false,
      baseDate: df.parse(timeSlot.date),
      selectedSchoolTimeSlots: timeSlots,
    });
  }

  private async handleSubmit() {
    const { inputs, actions, onSubmit, onRestart } = this.props;
    const { selectedSchoolTimeSlots } = this.state;
    try {
      await actions.handleSubmit(
        inputs.reservationId!,
        selectedSchoolTimeSlots!,
        inputs.merchantId ?? '',
      );
      onSubmit();
    } catch (e) {
      if (e instanceof Inputs.RestartReservationError) {
        onRestart();
      }
    }
  }

  public render() {
    const {
      inputs: { lesson },
      settings: { showVacancy, timeSlots },
      postable,
      intl,
    } = this.props;
    const { baseDate, selecting, selectedSchoolTimeSlots } = this.state;

    if (!lesson) {
      return null;
    }

    return (
      <div>
        {!selecting ? (
          <div>
            <LabeledContainer
              label={intl.formatMessage({
                id: 'books.formsLabel.selectedBooks',
              })}
              contentsPadding={true}
              paddingTop={true}
            >
              <BooksSelectedMenu
                name={lesson.name}
                options={[]}
                changeTarget={() => this.handleChangeTarget(true, false)}
              />
            </LabeledContainer>
            <LabeledContainer
              label={intl.formatMessage({
                id: 'books.booksMenu.bookDate',
              })}
              paddingTop={true}
            >
              {selectedSchoolTimeSlots ? (
                <BooksTimeItem
                  date={selectedSchoolTimeSlots[0].date}
                  startTime={selectedSchoolTimeSlots[0].startTime}
                  endTime={selectedSchoolTimeSlots[0].endTime}
                  instructors={selectedSchoolTimeSlots[0].instructors.map(
                    (i) => i.name,
                  )}
                  withPadding={true}
                  onClick={this.handleStartSelectingDate}
                />
              ) : (
                <BooksTextButton onClick={this.handleStartSelectingDate}>
                  {intl.formatMessage({
                    id: 'books.forms.addDateAndTime',
                  })}
                </BooksTextButton>
              )}
            </LabeledContainer>
            <div className="px-[18px] py-5">
              <RSButton
                variant="basic"
                isBlock
                isLoading={!postable}
                disabled={!selectedSchoolTimeSlots || !postable}
                onClick={this.handleSubmit}
              >
                {intl.formatMessage({ id: 'books.submitLabel.next' })}
              </RSButton>
            </div>
          </div>
        ) : (
          <InputSchoolCalendarDateTimePicker
            postable={postable}
            selection={'single'}
            baseDate={baseDate}
            timeSlots={timeSlots}
            showVacancy={showVacancy}
            onChange={this.handleChangeTimeSlots}
            onChangeBaseDate={this.handleChangeBaseDate}
            selectedTimeSlots={
              selectedSchoolTimeSlots ? selectedSchoolTimeSlots : []
            }
          />
        )}
      </div>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: I.Dispatch & B.Dispatch) => ({
  actions: {
    handleClearTargets: (course: boolean, options: boolean, staff?: boolean) =>
      Inputs.clearBookingTargets({ course, options, staff })(dispatch),
    handleSubmit: (
      reservationId: number,
      schoolTimeSlots: IInputs.SchoolTimeSlot[],
      merchantPublicId: string,
    ) =>
      Inputs.inputBookingSchoolTimeSlot(
        reservationId,
        schoolTimeSlots,
        merchantPublicId,
      )(dispatch),
    clearDateTimePicker: () => Actions.clearDateTimePicker()(dispatch),
    setDateTimePicker: (q: DateTimePickerQuery) =>
      Actions.setDateTimePicker(q)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(InputSchoolCalendar));
